import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useSelector } from "react-redux";

import moment from "moment";
import NumberFormat from "react-number-format";

import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import "../EstadoResultado/estadoResultado.css";

import { Tooltip } from "@mui/material";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

import { Breadcrumb } from "../Breadcrumb";
import { useFlow } from "./useFlow";

const compact = new Intl.NumberFormat("es-PE", { notation: "compact", maximumFractionDigits: 1 });

const limitRow = (row, count, balance = false) => {
  const values = row.values.slice(0, count + 1);
  const value = balance
    ? values[values.length - 1]?.value
    : values.reduce((sum, item) => sum + Number(item.value || 0), 0);
  return {
    ...row,
    values: [...values, { id: "horizon-total", value }],
    detail: row.detail?.map(item => limitRow(item, count)),
  };
};

export const FlujoCajaNew = () => {
  const { months_report, data_report, savedOK, hasControlledAccounts, reportStatus, initGetAllFlowNew, initEditFlow } = useFlow();
  const infoMoney = useSelector((state) => state.general.info);
  const [horizon, setHorizon] = useState(3);
  const [filters, setFilters] = useState({
    date_report: new Date(),
    cash_flow_type: "flujo_caja",
    show_flow_2: false,
  });

  useEffect(() => {
    initGetAllFlowNew(filters);
  }, [filters, initGetAllFlowNew]);

  const months = months_report.slice(1, horizon + 1);
  const values = id => data_report.find(row => Number(row.id) === id)?.values || [];
  const income = values(1);
  const expense = values(5);
  const balance = values(9);
  const bankOpening = Number(values(10)[0]?.value);
  const previousIncome = Number(income[0]?.value);
  const previousExpense = Number(expense[0]?.value);
  const chart = months.map(({ start_date }, index) => ({
    label: moment(start_date).locale("es").format("MMM YYYY"),
    income: Number(income[index + 1]?.value),
    expense: Number(expense[index + 1]?.value),
    balance: Number(balance[index + 1]?.value),
  })).filter(month => [month.income, month.expense, month.balance].every(Number.isFinite));
  const openingReady = [bankOpening, previousIncome, previousExpense].every(Number.isFinite);
  const projectedOpening = bankOpening + previousIncome - previousExpense;
  const ready = chart.length > 0 && chart.length === months.length;
  const firstNegative = chart.find(month => month.balance < 0);
  const lowest = chart.reduce((minimum, month) => !minimum || month.balance < minimum.balance ? month : minimum, null);
  const amount = value => (
    <NumberFormat displayType="text" value={value}
      thousandSeparator={infoMoney?.local_separacion_miles || ","}
      decimalSeparator={infoMoney?.local_separacion_decimales || "."}
      prefix={`${infoMoney?.local_simbolo || "S/"} `} decimalScale={2} fixedDecimalScale />
  );

  return (
    <div className="p-container er-report">
      <div className="mt-3">
        <Breadcrumb routes={[
          { id: 1, name: "Reportes", link: "/reportes", active: false },
          { id: 2, name: "Flujo de caja proyectado", link: "/reportes/fcnew", active: true },
        ]} />
      </div>
      <header className="er-heading">
        <div>
          <span className="er-eyebrow">Reportes financieros</span>
          <h1 className="p-title">Flujo de caja proyectado</h1>
          <p>¿Cuándo podría faltarnos caja y qué cobros o pagos explican la proyección?</p>
        </div>
        <div className="er-filters d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label" htmlFor="cash-horizon">Horizonte</label>
            <select className="form-select" id="cash-horizon" value={horizon} onChange={event => setHorizon(Number(event.target.value))}>
              <option value={3}>3 meses</option>
              <option value={6}>6 meses</option>
              <option value={12}>12 meses</option>
            </select>
          </div>
          <button type="button" className="btn p-btn-secondary" onClick={() => initGetAllFlowNew(filters)}>
            <RefreshCw size={14} className="me-1" aria-hidden="true" /> Actualizar
          </button>
        </div>
      </header>
      <p className="er-source mb-3">
        El horizonte incluye el mes en curso. El saldo inicial proyectado combina bancos con los ingresos y egresos pendientes de meses anteriores.
        Los ingresos se muestran netos y los egresos incluyen IGV.
      </p>
      {reportStatus === "loading" && <p role="status">Cargando flujo de caja proyectado…</p>}
      {reportStatus === "error" && <p role="alert">No se pudo cargar la proyección. Pulsa Actualizar para reintentar.</p>}
      {reportStatus === "ready" && !data_report.length && <p role="status">No hay datos de proyección disponibles.</p>}
      {reportStatus === "ready" && data_report.length > 0 && !ready && <p role="status">La proyección mensual está incompleta.</p>}
      {hasControlledAccounts === false && <p className="er-insight">No hay cuentas con control de saldo activo.</p>}
      {openingReady && (
          <p className="er-source mb-3">
            Saldo inicial proyectado: bancos {amount(bankOpening)} + ingresos pendientes anteriores {amount(previousIncome)}
            {" − egresos pendientes anteriores "}{amount(previousExpense)} = <strong>{amount(projectedOpening)}</strong>.
          </p>
      )}
      {ready && (
        <>
          <div className="row g-2 mb-3">
            {[
              ["Saldo inicial proyectado", projectedOpening],
              ["Ingresos previstos", chart.reduce((sum, month) => sum + month.income, 0)],
              ["Egresos previstos", chart.reduce((sum, month) => sum + month.expense, 0)],
              ["Saldo final proyectado", chart[chart.length - 1].balance],
            ].map(([label, value]) => (
              <div className="col-6 col-lg-3" key={label}>
                <div className="er-chart h-100 mb-0">
                  <div className="er-source">{label}</div>
                  <strong className={`fs-5 ${Number(value) < 0 ? "text-danger" : ""}`}>{amount(value)}</strong>
                </div>
              </div>
            ))}
          </div>
          <p className="er-insight">
            {firstNegative && <>Primer saldo negativo en <strong>{firstNegative.label}</strong>, con un faltante de <strong>{amount(-firstNegative.balance)}</strong>. </>}
            Menor saldo proyectado en <strong>{lowest.label}</strong>: <strong>{amount(lowest.balance)}</strong>.
          </p>
          <figure className="er-chart">
            <figcaption className="fw-bold mb-2">Ingresos, egresos y saldo proyectado</figcaption>
            <div className="er-chart-scroll">
              <ResponsiveContainer width="100%" height={280} minWidth={Math.max(380, chart.length * 75)}>
                <ComposedChart data={chart}>
                  <CartesianGrid vertical={false} stroke="#ece4ee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={value => compact.format(value)} />
                  <ChartTooltip formatter={value => amount(value)} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#675c6b" />
                  <Bar dataKey="income" name="Ingresos" fill="var(--primary-color)" maxBarSize={36} isAnimationActive={false} />
                  <Bar dataKey="expense" name="Egresos" fill="#b45c26" maxBarSize={36} isAnimationActive={false} />
                  <Line dataKey="balance" name="Saldo proyectado" stroke="#16755d" strokeWidth={2} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </figure>
        </>
      )}
      {data_report.length > 0 && months.length > 0 && (
        <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Detalle de la proyección">
          <table className="table er-table mb-0">
            <thead>
              <tr>
                <th scope="col">Concepto</th>
                <th scope="col" className="text-end text-nowrap">Saldo inicial y pendientes anteriores</th>
                {months.map(month => (
                  <th scope="col" key={month.start_date} className="text-end text-nowrap">
                    {moment(month.start_date).locale("es").format("MMM YYYY")}
                  </th>
                ))}
                <th scope="col" className="text-end text-nowrap">Total del horizonte</th>
              </tr>
            </thead>
            <tbody>
              {data_report.map(row => (
                <FlowRow key={row.id} it={limitRow(row, months.length, Number(row.id) === 9)}
                  projectedOpening={Number(row.id) === 10 && openingReady ? projectedOpening : undefined}
                  handleEditFlow={initEditFlow} savedOK={savedOK} infoMoney={infoMoney}
                  filters={filters} setFilters={setFilters} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const FlowRow = ({
  it,
  projectedOpening,
  handleEditFlow,
  savedOK,
  infoMoney,
  filters,
  setFilters,
}) => {
  const [openEdit, setOpenEdit] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const { local_simbolo, local_separacion_miles, local_separacion_decimales } =
    infoMoney;

  useEffect(() => {
    setOpenEdit(false);
  }, [savedOK]);

  const handleShowFlow = (e) => {
    setFilters({
      ...filters,
      show_flow_2: e.target.checked,
    });
  };

  return (
    <>
      <tr
        style={{
          backgroundColor: it?.tipo === "title" ? "#FCF1FF" : "",
          fontWeight: it?.tipo === "title" ? "" : "",
          textDecorationLine:
            !filters.show_flow_2 && parseInt(it?.id) === 2
              ? "line-through"
              : "",
        }}
      >
        <th
          className="min-w-60"
          style={{
            fontWeight: it?.tipo === "title" ? "bold" : "",
            textDecoration: it?.tipo === "title" ? "" : "",
          }}
        >
          <div
            className="d-flex justify-content-start align-items-center text-start"
            style={{ width: "200px" }}
          >
            {it?.has_detail && !openDetail ? (
              <button onClick={() => setOpenDetail(!openDetail)}>
                <ChevronRight className="h-4 w-4 pointer" />
              </button>
            ) : (
              ""
            )}

            {it?.has_detail && openDetail ? (
              <button onClick={() => setOpenDetail(!openDetail)}>
                <ChevronDown className="h-4 w-4 pointer" />
              </button>
            ) : (
              ""
            )}

            {it?.is_detail ? (
              <span className="ms-3">
                <i className="bi bi-arrow-return-right"></i>
                <label className="ms-1">{it?.referencia}</label>
              </span>
            ) : (
              <span
                className="ms-2"
                style={{
                  color: it?.tipo === "title" ? "#4a24aa" : "#8E24AA",
                  fontWeight: "bold",
                }}
              >
                <label className="d-flex">
                  {parseInt(it?.id) === 2 ? (
                    <input
                      type="checkbox"
                      name=""
                      id=""
                      onChange={handleShowFlow}
                      checked={filters.show_flow_2}
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    ""
                  )}
                  {/* <span className="ms-1">{it?.referencia} ({it.detail?.length})</span> */}
                  <span className="ms-1">{it?.referencia}</span>
                </label>
              </span>
            )}
          </div>
        </th>
        {it?.values?.map((item, index) => (
          <td key={item.id} className="min-w-24 text-end text-nowrap">
            {parseFloat(it.id) === 10 || parseFloat(it.id) === 11 ? (
              <NumberFormat
                style={{ textAlign: "right" }}
                displayType={!openEdit ? "text" : ""}
                value={index === 0 && projectedOpening !== undefined ? projectedOpening : item?.value ?? "-"}
                thousandSeparator={local_separacion_miles}
                decimalSeparator={local_separacion_decimales}
                prefix={`${local_simbolo} `}
                onValueChange={(value) => handleEditFlow(value, item.id, it.id)}
                decimalScale={2}
                fixedDecimalScale={2}
              />
            ) : (
              <NumberFormat
                style={{
                  textAlign: "right",
                  color:
                    parseFloat(it.id) === 8 || parseFloat(it.id) === 9
                      ? item?.value > 0
                        ? "green"
                        : item?.value < 0
                          ? "red"
                          : ""
                      : "",
                }}
                displayType={"text"}
                value={item?.value ?? "-"}
                thousandSeparator={local_separacion_miles}
                decimalSeparator={local_separacion_decimales}
                prefix={`${local_simbolo} `}
                onValueChange={(value) => handleEditFlow(value, item.id, it.id)}
                decimalScale={2}
                fixedDecimalScale={2}
              />
            )}
          </td>
        ))}
      </tr>

      {openDetail &&
        it?.detail?.map((item, index) => (
          <DetailFlowRow
            key={index}
            it={item}
            infoMoney={infoMoney}
            idFlow={it.id}
          />
        ))}
    </>
  );
};

const DetailFlowRow = ({ it, infoMoney, idFlow }) => {
  const { local_simbolo, local_separacion_miles, local_separacion_decimales } =
    infoMoney;

  return (
    <tr
      style={{
        backgroundColor: it?.tipo === "title" ? "#f8f7f7" : "",
        fontWeight: it?.tipo === "title" ? "bold" : "",
      }}
    >
      <th
        scope="row"
        style={{
          fontWeight: it?.tipo === "title" ? "bold" : "",
          textDecoration: it?.tipo === "title" ? "underline" : "",
        }}
      >
        <div
          className="d-flex justify-content-start align-items-center text-start"
          style={{ width: "200px" }}
        >
          {parseInt(idFlow) === 10 ? (
            <Link
              to={`/banks/acc`}
              target="_blank"
              className="font-bold hover:underline text-gray-500 hover:text-gray-500 cursor-pointer flex flex-col items-start"
            >
              <span>{it?.referencia}</span>
              <span className="text-[10px]">
                {parseInt(it.id_moneda) === 4 ? "USD" : "SOLES"}
              </span>
            </Link>
          ) : (
            <Link
              to={`/projects/${it?.ppto_id}`}
              target="_blank"
              className="font-bold hover:underline text-gray-500 hover:text-gray-500"
            >
              <div className="ms-3 text-start cursor-pointer">
                <label className="uppercase text-[12px] text-gray-700">
                  {it?.referencia}
                </label>
                <br />
                <label className="text-[10px] font-semibold text-gray">
                  - {it?.ppto_numero}
                  {" / "}
                  {it?.ppto_referencia}
                  {" / "}
                  {it?.ppto_id}
                </label>
              </div>
            </Link>
          )}
        </div>
      </th>
      {it?.values?.map((item) => (
        <td key={item.id} className="text-end text-nowrap">
          {parseFloat(idFlow) === 10 || parseFloat(idFlow) === 11 ? (
            <span className="text-end">
              <NumberFormat
                displayType="text"
                value={item?.value ?? "-"}
                thousandSeparator={
                  it.separacion_miles || local_separacion_miles
                }
                decimalSeparator={
                  it.separacion_decimales || local_separacion_decimales
                }
                prefix={`${it.simbolo || local_simbolo} `}
                decimalScale={2}
                fixedDecimalScale={0}
              />
            </span>
          ) : (
            <>
              {parseInt(it?.ppto_id_moneda) === 4 && item?.value ? (
                <Tooltip
                  title={`S/ ${parseFloat(item?.value) * parseFloat(it?.ppto_valor_tipo_cambio)}`}
                  placement="right"
                >
                  <i
                    className="bi bi-info-circle ms-1"
                    style={{ fontSize: "10px" }}
                  ></i>
                </Tooltip>
              ) : (
                // <NumberFormat
                //     displayType={'text'}
                //     value={it?.ppto_valor_tipo_cambio || '-'}
                //     thousandSeparator={it?.separacion_miles}
                //     decimalSeparator={it?.separacion_decimales}
                //     prefix={`S/ `}
                //     decimalScale={2}
                //     fixedDecimalScale={0}
                //     style={{ fontSize: '9px' }}
                // />
                ""
              )}
              <br />
              <NumberFormat
                style={{
                  textAlign: "right",
                  color:
                    parseFloat(it.id) === 8 || parseFloat(it.id) === 9
                      ? item?.value > 0
                        ? "green"
                        : item?.value < 0
                          ? "red"
                          : ""
                      : "",
                }}
                displayType={"text"}
                value={item?.value ?? "-"}
                thousandSeparator={it?.separacion_miles}
                decimalSeparator={it?.separacion_decimales}
                prefix={`${it?.simbolo} `}
                decimalScale={2}
                fixedDecimalScale={0}
              />
            </>
          )}
        </td>
      ))}
    </tr>
  );
};

// const DateFilters = ({ filters, handleChangeDate }) => {

//     return (
//         <div className='d-flex mt-3'>
//             <div className='p-text-date-year'>
//                 <LocalizationProvider locale={es} dateAdapter={AdapterDateFns}>
//                     <DatePicker
//                         views={['year']}
//                         label="Año"
//                         openTo="year"
//                         name='date_report'
//                         value={filters.date_report}
//                         onChange={handleChangeDate}
//                         renderInput={(params) => <TextField {...params} helperText={null} />}
//                     />
//                 </LocalizationProvider>
//             </div>
//         </div>
//     )

// }
